import { Selector } from '@ngxs/store';
import { TableState } from './table.state';
import {
  TableStateModel,
  ApprovalStateModel,
  TransactionStateModel,
  ITableStateModel,
  IState,
  IAccountMetadata,
  IRoles,
  IPermissions,
  IReferralMetadata,
  IUpdateApprovalMetadata,
  IAccountRMMetadata,
} from './table.state.model';

export class TableSelectors {
  // Generic table selectors - these can be used for any table
  @Selector([TableState])
  static getTableData(tableKey: string) {
    return (state: IState): any[] => {
      const tableState = TableSelectors.getTableState(state.table, tableKey);
      return tableState?.data || [];
    };
  }

  @Selector([TableState])
  static getTableLoading(tableKey: string) {
    return (state: IState): boolean => {
      const tableState = TableSelectors.getTableState(state.table, tableKey);
      return tableState?.loading || false;
    };
  }

  @Selector([TableState])
  static getTableError(tableKey: string) {
    return (state: IState): string | null => {
      const tableState = TableSelectors.getTableState(state.table, tableKey);
      return tableState?.error || null;
    };
  }

  @Selector([TableState])
  static getTableTotalCount(tableKey: string) {
    return (state: IState): number => {
      const tableState = TableSelectors.getTableState(state.table, tableKey);
      return tableState?.totalCount || 0;
    };
  }

  @Selector([TableState])
  static getTableFilters(tableKey: string) {
    return (state: IState): Record<string, any> => {
      const tableState = TableSelectors.getTableState(state.table, tableKey);
      return tableState?.filters || {};
    };
  }

  @Selector([TableState])
  static getTableSelectedItems(tableKey: string) {
    return (state: IState): any[] => {
      const tableState = TableSelectors.getTableState(state.table, tableKey);
      return tableState?.selectedItems || [];
    };
  }

  // Helper method to get table state by key
  private static getTableState(
    state: TableStateModel,
    tableKey: string
  ): ITableStateModel | null {
    switch (tableKey) {
      case 'approval':
        return state.approval;

      case 'transactions':
        return state.transactions || null;

      case 'accounts':
        return state.accounts || null;
      case 'accountsRM':
        return state.accountsRM || null;

      case 'mobileUsers':
        return state.mobileUsers || null;
      case 'systemUsers':
        return state.systemUsers || null;
      case 'roles':
        return state.roles || null;
      case 'orders':
        return state.orders || null;
      case 'clientWF':
        return state.clientWF || null;
      case 'activeClients':
        return state.activeClients || null;
      case 'inactiveClients':
        return state.inactiveClients || null;
      case 'portfolio':
        return state.portfolio || null;
      case 'general':
        return state.general || null;
      case 'mutualFunds':
        return state.mutualFunds || null;
      case 'fixedDeposits':
        return state.fixedDeposits || null;
      case 'tbills':
        return state.tbills || null;
      case 'bonds':
        return state.bonds || null;
      case 'commercialPapers':
        return state.commercialPapers || null;
      case 'pmsProducts':
        return state.pmsProducts || null;
      case 'walletTransactions':
        return state.walletTransactions || null;
      case 'notificationLogs':
        return state.notificationLogs || null;
      case 'cashTransactions':
        return state.cashTransactions || null;
      case 'referrals':
        return state.referrals || null;
      case 'identityLog':
        return state.identityLog || null;
      case 'updateApprovals':
        return state.updateApprovals || null;
      case 'auditLog':
        return state.auditLog || null;
      case 'orderApprovals':
        return state.orderApprovals || null;
      case 'relationshipManagers':
        return state.relationshipManagers || null;
      case 'configurations':
        return state.configurations || null;
      case 'mnmmfStatements':
        return state.mnmmfStatements || null;
      case 'banks':
        return state.banks || null;
      default:
        return null;
    }
  }

  // Pre-configured selectors for common tables
  // Approval selectors (backward compatibility)
  @Selector([TableState])
  static getApprovals(state: TableStateModel): any[] {
    return state.approval.approvals || state.approval.data || [];
  }

  @Selector([TableState])
  static getApprovalLoading(state: TableStateModel): boolean {
    return state.approval.loading;
  }

  @Selector([TableState])
  static getApprovalError(state: TableStateModel): string | null {
    return state.approval.error;
  }

  @Selector([TableState])
  static getApprovalState(state: TableStateModel): ApprovalStateModel {
    return state.approval;
  }

  @Selector([TableState])
  static getApprovalTotalCount(state: TableStateModel): number {
    return state.approval.totalCount || 0;
  }

  // Transactions selectors
  @Selector([TableState])
  static getTransactions(state: TableStateModel): any[] {
    return state.transactions?.data || [];
  }

  @Selector([TableState])
  static getTransactionsLoading(state: TableStateModel): boolean {
    return state.transactions?.loading || false;
  }

  @Selector([TableState])
  static getTransactionsError(state: TableStateModel): string | null {
    return state.transactions?.error || null;
  }

  @Selector([TableState])
  static getTransactionsState(
    state: TableStateModel
  ): TransactionStateModel | null {
    return state.transactions || null;
  }

  // Global selectors
  // @Selector([TableState])
  // static getGlobalLoading(state: TableStateModel): boolean | null {
  //   return state.loading;
  // }

  // account selectors
  @Selector([TableState])
  static getAccountsMetadata(): any {
    return (state: IState): IAccountMetadata | null => {
      return state.table?.accounts?.accountMetadata || null;
    };
  }

  @Selector([TableState])
  static getAccountsRMMetadata(): any {
    return (state: IState): IAccountRMMetadata | null => {
      return state.table?.accountsRM?.accountRMMetadata || null;
    };
  }

  // system user selectors
  static getSystemUsersRoles(): any {
    return (state: IState): IRoles[] | null => {
      return state.table.systemUsers?.roles || null;
    };
  }
  static getPermissions(): any {
    return (state: IState): IPermissions[] | null => {
      return state.table.roles?.permissions || null;
    };
  }

  // access bank selectors
  static getAccessBankBalance(): any {
    return (state: IState): number | null => {
      return state.table.walletTransactions?.accessBankBalance || null;
    };
  }

  // verto bank selectors
  static getVertoBankBalance(): any {
    return (state: IState): number | null => {
      return state.table.walletTransactions?.vertoBalance || null;
    };
  }

  // paystack bank selectors
  static getPaystackBankBalance(): any {
    return (state: IState): number | null => {
      return state.table.walletTransactions?.paystackBalance || null;
    };
  }

  // providus bank selectors
  static getProvidusBankBalance(): any {
    return (state: IState): number | null => {
      return state.table.walletTransactions?.providusBalance || null;
    };
  }

  // investment cash selectors
  static getInvestmentCashBalance(): any {
    return (state: IState): number | null => {
      return state.table.cashTransactions?.investmentCash || null;
    };
  }

  // wallet cash selectors
  static getWalletCashBalance(): any {
    return (state: IState): number | null => {
      return state.table.cashTransactions?.walletCash || null;
    };
  }

  // referral metadata selectors
  static getReferralMetadata(): any {
    return (state: IState): IReferralMetadata | null => {
      return state.table.referrals?.referralMetadata || null;
    };
  }

  // update approval metadata selectors
  static getUpdateApprovalMetadata(): any {
    return (state: IState): IUpdateApprovalMetadata | null => {
      return state.table.updateApprovals?.updatesMetadata || null;
    };
  }
}
